import { ref, computed } from 'vue'
import useEventEmitter from '@/_core/hooks/useEventEmitter'
import useApiRequest from '@/_core/hooks/useApiRequest'
import { getUserItems } from '@/services/catalog'
import { BOOKS_PER_PAGE_CATALOG_DESKTOP, USER_ITEM_TYPE_BOOK, USER_ITEM_TYPE_VPR } from '@/config'
import CatalogItemBundle from '@/models/CatalogItemBundle'
import store from '@/store'
import moment from 'moment'

const initialQueryParams = {
  level: null,
  types: null,
  subjects_id: null,
  search: null,
  bought: null,
  favorite: null,
  page_size: 0
}

export default function CatalogController (limit = BOOKS_PER_PAGE_CATALOG_DESKTOP) {
  const { $on, $off, $emit } = useEventEmitter()

  // Filters logic
  const filterParams = ref({ ...initialQueryParams, page_size: limit })
  const setFilter = params => {
    Object.keys(initialQueryParams).forEach(key => {
      if (params[key] || params[key] === 0) {
        filterParams.value[key] = params[key]
      } else {
        filterParams.value[key] = null
      }
    })
    filterParams.value.page_size = limit
    $emit('update-filter')
  }

  const resetFilter = () => {
    filterParams.value = { ...initialQueryParams, limit }
  }

  // Items
  const items = ref([])

  const nextPage = ref(null)

  const hasMoreItems = computed(() => Boolean(nextPage.value))

  const itemsRequest = useApiRequest(getUserItems)
  itemsRequest.$on('success', () => {
    items.value = itemsRequest.data.results
    nextPage.value = itemsRequest.data.next
    $emit('requestItems-success', itemsRequest.data)
  })
  itemsRequest.$on('error', () => {
    $emit('requestItems-error', itemsRequest.errors)
  })
  const requestItems = async () => {
    await itemsRequest.request(filterParams.value)
  }
  $on('update-filter', requestItems)

  // Bundles
  const booksItems = computed(() => items.value.filter(el => el.type === USER_ITEM_TYPE_BOOK))
  const vprsItems = computed(() => items.value.filter(el => el.type === USER_ITEM_TYPE_VPR))
  const subscriptions = computed(() => store.user.profile?.subscriptions)

  const bundleItems = computed(() => {
    const keys = Array.from(new Set(booksItems.value.map(el => el.nomenklatura)))
    return keys.map(key => {
      const items = booksItems.value.filter(el => el.nomenklatura === key)
      const label = items[0].data.subject.label
      const purchaseLink = items[0].purchaseLink
      const nomenklatura = items[0].nomenklatura
      const purchaseText = items[0].purchaseText
      const subscription = subscriptions.value?.find(el => el.nomenklatura === nomenklatura)
      const bought = subscription?.bought ?? false
      const expiresValue = subscription?.expires ?? ''
      const expires = expiresValue ? moment(expiresValue).format('DD.MM.YYYY') : null

      return new CatalogItemBundle({
        label,
        purchaseLink,
        purchaseText,
        nomenklatura,
        bought,
        expires,
        items
      })
    })
  })

  // More items
  const getMoreItemsRequest = useApiRequest(getUserItems)
  getMoreItemsRequest.$on('success', () => {
    items.value = [...items.value, ...getMoreItemsRequest.data.results]
    nextPage.value = getMoreItemsRequest.data.next
    $emit('requestItems-success', getMoreItemsRequest.data)
  })
  getMoreItemsRequest.$on('error', () => {
    $emit('requestItems-error', getMoreItemsRequest.errors)
  })
  const requestGetMoreItems = async () => {
    if (!nextPage.value) return
    await getMoreItemsRequest.request(filterParams.value, nextPage.value)
  }

  // Output.
  const output = {
    items,
    vprsItems,
    bundleItems,
    setFilter,
    resetFilter,
    hasMoreItems,
    requestItems,
    requestGetMoreItems,
    isLoading: computed(() => itemsRequest.isLoading),
    isLoadingMoreItems: computed(() => getMoreItemsRequest.isLoading),
    $on,
    $off
  }

  return output
}
